import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  BaseBotFormValues,
  BotFormType,
  Error,
  GridsData,
  GridType,
  HedgingMode,
  HedgingGridsData,
  Steps,
} from "@shared/features/bots/BotForm/common/types";
import {
  getBotGridsData,
  getGridsDataFromStrategy,
  getGridsParsedBasePrice,
  getGridsPriceDifference,
  getIsReverse,
  isHedgingRangeStrategyMixed,
  getPriceRangeDistancePercent,
  getStrategyOrders,
  getUpdatedStrategyAndGridsData,
  validateGridsData,
  validateHedgingGridsData,
} from "@shared/features/bots/BotForm/common/utils";
import {
  DEFAULT_GRID_BOT_STRATEGY,
  DEFAULT_GRID_PRICE_RANGE_RATIO,
  multiToggleValue,
} from "@shared/features/bots/BotForm/common/constants";
import { isEqual } from "lodash";
import { isSymbol } from "@shared/types/utils";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import { round, toFixed } from "@shared/utils/numbers";
import { useTheme } from "styled-components";
import {
  AmountType,
  BotData,
  BotType,
  DCAStrategyData,
  OpenMessage,
  PrecisionType,
  LanguageNameSpaces,
} from "@shared/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import {
  useAmountError,
  useBotFormDefaultAmount,
  useStrategyError,
} from "@shared/features/bots/BotForm/common/hooks";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const MIN_DEFAULT_NUM_ORDERS = 5;
export const HEDGING_SYMBOL_OVERRIDE_WARNING =
  "The hedging strategy can only be overridden when all the edited bots have the same symbol.";
export const HEDGING_RANGE_SYMBOL_OVERRIDE_WARNING =
  "The range hedging strategy can only be overridden when all the edited bots have the same symbol.";

interface useGridsSelectorProps {
  preserveExistingValues?: boolean;
  updateFromStrategy?: boolean;
  openMessage: OpenMessage;
  isOnboarding?: boolean;
  isCopyPublicBotSummary?: boolean;
}

const useGridsSelector = ({
  preserveExistingValues,
  updateFromStrategy,
  openMessage,
  isOnboarding,
  isCopyPublicBotSummary,
}: useGridsSelectorProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    botGeneralData,
    botFormType,
    botType,
    gridsData,
    setGridsData,
    hedgingGridsData,
    setHedgingGridsData,
    hedgingMode,
    setLongHedgingRatio,
    setEntryStrategy,
    addFieldToInitialize,
    isEdit,
    botAmountData,
    longShort,
    entryStrategy,
    account,
    symbols,
    initialized,
    fieldsToInitialize,
    addError,
    removeError,
    isAdvancedView,
    setTab,
    tab,
    getMinAmountByPrice,
    amountValue,
    setAmountValue,
    amountType,
    setAmountType,
    getExchangeSymbolPriceStatus,
    getExchangeToSymbolPricesStatus,
    removeFieldToInitialize,
    botsData,
  } = useContext(BotFormContext)!;
  const theme = useTheme();
  const isHedgingBot = botType === BotType.HEDGING;
  const activeGridsData = isHedgingBot ? hedgingGridsData : gridsData;
  const setActiveGridsData = isHedgingBot ? setHedgingGridsData : setGridsData;
  const entryStrategyRef =
    useRef<BaseBotFormValues["entryStrategy"]>(undefined);
  entryStrategyRef.current = entryStrategy;
  const isSymbolAndAccountValid =
    account && !isSymbol(account) && !isSymbol(symbols) && symbols.length > 0;
  const {
    currentData: symbolPriceData,
    isFetching: isFetchingSymbolPriceData,
  } = getExchangeSymbolPriceStatus;
  const bulkSymbolPrice = useMemo(() => {
    if (isSymbol(symbols) || !symbols.length) return;
    const symbol = symbols[0];
    return Object.values(getExchangeToSymbolPricesStatus.data || {}).reduce<
      number | undefined
    >((currentPrice, exchangeToPrice) => {
      if (currentPrice !== undefined) return currentPrice;
      const price = exchangeToPrice[symbol];
      if (price === undefined) return;
      const parsedPrice = parseFloat(price);
      return Number.isNaN(parsedPrice) ? undefined : parsedPrice;
    }, undefined);
  }, [getExchangeToSymbolPricesStatus.data, symbols]);
  const defaultRangeCurrentPrice = symbolPriceData?.data ?? bulkSymbolPrice;
  const isCopyPublic = botFormType === BotFormType.COPY_PUBLIC;
  const symbol =
    !!botGeneralData && "symbols" in botGeneralData
      ? botGeneralData?.symbols?.[0]
      : undefined;
  const copiedBotPriceData =
    sharedLib.sharedApi.bots.useGetExchangeSymbolPriceQuery(
      isCopyPublic && botGeneralData && symbol
        ? {
            exchange: (botGeneralData as BotData).exchange,
            symbol,
          }
        : skipToken
    );
  const originalBotSymbolPriceData = copiedBotPriceData.data;
  const lastUpdatedStrategyRef =
    useRef<BaseBotFormValues["entryStrategy"]>(undefined);
  const [initializedFormData, setInitializedFormData] = useState<
    { exchange: string; symbol?: string; isReverse?: boolean } | undefined
  >(undefined);
  const { getDefaultAmountValue, getAvailableAmount } =
    useBotFormDefaultAmount();
  const { getHelperData } = useFormHelperData();
  const { getMinimumPrecision } = usePrecisions();
  const isReverse = isHedgingBot ? false : getIsReverse(longShort);
  const bulkEditExchanges = Array.from(
    new Set(botsData?.map((bot) => bot.exchange).filter(Boolean))
  );
  const bulkEditPricePrecision =
    isHedgingBot &&
    botFormType === BotFormType.BULK_EDIT &&
    !isSymbol(symbols) &&
    symbols.length === 1 &&
    bulkEditExchanges.length
      ? getMinimumPrecision(PrecisionType.PRICE, symbols[0], bulkEditExchanges)
      : undefined;
  const pricePrecision =
    symbolPriceData?.precision.price ?? bulkEditPricePrecision;
  const strategyError = useStrategyError(OrderTypes.ENTRY);
  const { amountError: minAmountErrorMessage } = useAmountError(
    isOnboarding,
    isCopyPublicBotSummary
  );
  const generalGridsError = !minAmountErrorMessage && strategyError;
  const shouldValidateGridsData =
    !isHedgingBot || hedgingMode === HedgingMode.RANGE;

  const rangeStrategySectionIsMultiValue = isHedgingRangeStrategyMixed({
    hedgingMode,
    entryStrategy,
    hedgingGridsData,
  });

  const validationData = useMemo(
    () =>
      !shouldValidateGridsData
        ? {
            isValid: true,
            numGridsError: undefined,
            priceErrorMessage: undefined,
            exposureErrorMessage: undefined,
          }
        : isHedgingBot
        ? validateHedgingGridsData(hedgingGridsData)
        : {
            ...validateGridsData(gridsData),
            exposureErrorMessage: undefined,
          },
    [shouldValidateGridsData, isHedgingBot, hedgingGridsData, gridsData]
  );
  const { isValid, numGridsError, priceErrorMessage, exposureErrorMessage } =
    validationData;

  useEffect(() => {
    if (!isValid) addError(Error.GRIDS_DATA);
    else removeError(Error.GRIDS_DATA);
  }, [isValid]);

  const onStrategyChange = (strategy: BaseBotFormValues["entryStrategy"]) => {
    setEntryStrategy(strategy);
    addFieldToInitialize?.("entryStrategy");
    lastUpdatedStrategyRef.current = strategy;
  };

  const setNumOrdersAndDefaultAmount = (
    baseStrategy: DCAStrategyData,
    baseGridsData: GridsData
  ) => {
    if (isHedgingBot || !symbolPriceData || !botAmountData) return;
    let defaultNumOrders = !isSymbol(baseStrategy.number_of_orders)
      ? baseStrategy.number_of_orders
      : undefined;
    const availableAmount = getAvailableAmount();
    let defaultAmount: number | undefined = undefined;
    while (defaultNumOrders && defaultNumOrders > MIN_DEFAULT_NUM_ORDERS) {
      const parsedValidationOrders = getStrategyOrders(
        botAmountData,
        symbolPriceData.data,
        symbolPriceData.precision.price || 0,
        isReverse,
        OrderTypes.ENTRY,
        baseStrategy,
        getMinAmountByPrice,
        baseGridsData
      );
      defaultAmount = getDefaultAmountValue(
        undefined,
        parsedValidationOrders.slice(1)
      )?.defaultAmountValue;
      if (
        defaultAmount !== undefined &&
        defaultAmount <= availableAmount * 0.1
      ) {
        break;
      }
      defaultNumOrders -= 5;
      baseStrategy.number_of_orders = defaultNumOrders;
      baseGridsData.numGrids = defaultNumOrders.toString();
      baseStrategy.price_difference = getGridsPriceDifference(
        baseGridsData,
        isReverse
      );
    }

    setAmountType(AmountType.FIXED_USD_AMOUNT);
    setAmountValue(defaultAmount);
    addFieldToInitialize?.("amountValue");
  };

  const getDefaultBasePrice = (currentPrice: number) => {
    const baselinePriceMultiplier = isReverse ? 1 - 0.03 : 1 + 0.03;
    return toFixed(currentPrice * baselinePriceMultiplier, pricePrecision || 0);
  };

  const getDefaultGridsDataAndStrategy = (
    defaultNumGrids?: GridsData["numGrids"]
  ): [GridsData, DCAStrategyData] => {
    const numGrids =
      defaultNumGrids ?? DEFAULT_GRID_BOT_STRATEGY.number_of_orders?.toString();
    const basePrice = getDefaultBasePrice(defaultRangeCurrentPrice!);
    const rangeMultiplier = 1 - DEFAULT_GRID_PRICE_RANGE_RATIO;
    const minPrice = isReverse
      ? basePrice
      : toFixed(basePrice * rangeMultiplier, pricePrecision || 0);
    const maxPrice = isReverse
      ? toFixed(basePrice / rangeMultiplier, pricePrecision || 0)
      : basePrice;

    const defaultGridsData: GridsData = {
      gridType: GridType.ARITHMETIC,
      priceRange: { min: minPrice.toString(), max: maxPrice.toString() },
      numGrids: numGrids?.toString(),
    };
    const defaultStrategy = {
      ...DEFAULT_GRID_BOT_STRATEGY,
      price_difference: getGridsPriceDifference(defaultGridsData, isReverse),
    };
    if (defaultNumGrids && !isSymbol(defaultNumGrids))
      defaultStrategy.number_of_orders = parseInt(defaultNumGrids);
    return [defaultGridsData, defaultStrategy];
  };

  const toHedgingGridsData = (
    baseGridsData: GridsData,
    resetExposureRatios?: boolean
  ): HedgingGridsData => ({
    ...baseGridsData,
    lowerPriceLongExposureRatio: resetExposureRatios
      ? "50"
      : hedgingGridsData?.lowerPriceLongExposureRatio ?? "50",
    upperPriceLongExposureRatio: resetExposureRatios
      ? "50"
      : hedgingGridsData?.upperPriceLongExposureRatio ?? "50",
  });

  const setDefaultGridsData = (
    initializeAmount?: boolean,
    resetHedgingExposureRatios?: boolean
  ) => {
    if (
      defaultRangeCurrentPrice === undefined ||
      (initializeAmount && !botAmountData)
    )
      return false;
    const [defaultGridsData, defaultStrategy] =
      getDefaultGridsDataAndStrategy();
    if (initializeAmount) {
      setNumOrdersAndDefaultAmount(defaultStrategy, defaultGridsData);
    }
    setActiveGridsData(
      isHedgingBot
        ? toHedgingGridsData(defaultGridsData, resetHedgingExposureRatios)
        : defaultGridsData
    );
    onStrategyChange(defaultStrategy);
    return true;
  };

  const handleDirectionChange = () => {
    if (
      isHedgingBot ||
      !botAmountData ||
      !symbolPriceData ||
      !activeGridsData ||
      !entryStrategy ||
      isSymbol(entryStrategy)
    )
      return false;
    let defaultGridsData: GridsData;
    let defaultStrategy: DCAStrategyData;
    if (isCopyPublic) {
      const { updatedStrategy, updatedGridsData } =
        getUpdatedStrategyAndGridsData(
          activeGridsData,
          entryStrategy,
          isReverse,
          pricePrecision
        );
      defaultGridsData = updatedGridsData;
      defaultStrategy = updatedStrategy;
    } else {
      [defaultGridsData, defaultStrategy] = getDefaultGridsDataAndStrategy(
        activeGridsData?.numGrids
      );
      const parsedValidationOrders = getStrategyOrders(
        botAmountData,
        symbolPriceData.data,
        symbolPriceData.precision.price || 0,
        isReverse,
        OrderTypes.ENTRY,
        defaultStrategy,
        getMinAmountByPrice,
        defaultGridsData
      );
      const defaultAmountData = isSymbol(amountType)
        ? undefined
        : getDefaultAmountValue(amountType, parsedValidationOrders.slice(1));
      if (
        defaultAmountData &&
        !isSymbol(amountValue) &&
        (!amountValue || defaultAmountData.minBotAmount > amountValue)
      ) {
        setAmountValue(defaultAmountData.defaultAmountValue);
        addFieldToInitialize?.("amountValue");
      }
    }
    setActiveGridsData(defaultGridsData);
    onStrategyChange(defaultStrategy);
    return true;
  };

  const getBotHedgingGridsData = (baseGridsData: GridsData) => {
    const botData = botGeneralData as BotData;
    return {
      ...baseGridsData,
      lowerPriceLongExposureRatio:
        botData.lower_price_long_exposure_ratio !== undefined
          ? toFixed(botData.lower_price_long_exposure_ratio * 100, 2).toString()
          : "50",
      upperPriceLongExposureRatio:
        botData.upper_price_long_exposure_ratio !== undefined
          ? toFixed(botData.upper_price_long_exposure_ratio * 100, 2).toString()
          : "50",
    };
  };

  const initializeEditBotGrids = (maxGridPrice?: number) => {
    if (!isSymbolAndAccountValid) return false;
    const botGridsData = getBotGridsData(
      maxGridPrice ?? (botGeneralData as BotData).max_grid_price,
      entryStrategy,
      isReverse,
      pricePrecision
    );
    if (botGridsData) {
      const parsedGridsData = isHedgingBot
        ? getBotHedgingGridsData(botGridsData)
        : botGridsData;
      const parsedGridsDataIsValid = isHedgingBot
        ? validateHedgingGridsData(parsedGridsData).isValid
        : validateGridsData(parsedGridsData).isValid;
      if (!parsedGridsDataIsValid) return false;
      setActiveGridsData(parsedGridsData);
      return true;
    }
    return false;
  };

  const initializeCopyPublicBotGrids = () => {
    const originalMaxPrice = (botGeneralData as BotData).max_grid_price;
    if (!originalMaxPrice || !originalBotSymbolPriceData || !symbolPriceData)
      return false;
    const originalPriceDifference =
      originalMaxPrice / originalBotSymbolPriceData.data;
    const newMaxPrice = round(
      symbolPriceData.data * originalPriceDifference,
      symbolPriceData.precision.price
    );
    return initializeEditBotGrids(newMaxPrice);
  };

  const getInitializationFlags = () => {
    if (!isSymbolAndAccountValid) return {};
    const baseShouldInitialize =
      botAmountData && !isFetchingSymbolPriceData && symbolPriceData;
    const accountOrSymbolChanged =
      initializedFormData &&
      (account.exchange !== initializedFormData.exchange ||
        symbols[0] !== initializedFormData.symbol);
    const directionChanged =
      initializedFormData && isReverse !== initializedFormData.isReverse;
    const shouldInitializeCreate =
      baseShouldInitialize &&
      !isEdit &&
      (!initializedFormData || accountOrSymbolChanged);
    const shouldInitializeEdit =
      [BotFormType.DUPLICATE, BotFormType.EDIT].includes(botFormType) &&
      initialized &&
      baseShouldInitialize &&
      !initializedFormData;
    const shouldInitializeCopyPublic =
      isCopyPublic &&
      initialized &&
      baseShouldInitialize &&
      originalBotSymbolPriceData &&
      (!initializedFormData || accountOrSymbolChanged);
    return {
      shouldInitializeCreate,
      shouldInitializeEdit,
      shouldInitializeCopyPublic,
      directionChanged,
    };
  };

  useEffect(() => {
    if (!symbolPriceData || !isSymbolAndAccountValid) return;
    const {
      shouldInitializeCreate,
      shouldInitializeEdit,
      shouldInitializeCopyPublic,
      directionChanged,
    } = getInitializationFlags();
    let initializedGrids = false;
    if (shouldInitializeCreate) {
      initializedGrids =
        preserveExistingValues &&
        !initializedFormData &&
        !isHedgingBot &&
        !!gridsData?.numGrids &&
        !!gridsData?.priceRange?.min &&
        !!gridsData?.priceRange?.max
          ? true
          : setDefaultGridsData(!isHedgingBot);
    } else if (shouldInitializeEdit) {
      initializedGrids = initializeEditBotGrids();
      if (!initializedGrids && isHedgingBot) {
        initializedGrids = setDefaultGridsData(false);
      }
    } else if (shouldInitializeCopyPublic) {
      initializedGrids = initializeCopyPublicBotGrids();
    } else if (directionChanged) {
      initializedGrids = handleDirectionChange();
    }
    if (initializedGrids) {
      setInitializedFormData({
        symbol: symbols[0],
        exchange: account.exchange,
        isReverse,
      });
    }
  }, [
    botAmountData,
    symbolPriceData,
    activeGridsData,
    entryStrategy,
    symbols,
    account,
    isSymbolAndAccountValid,
    isHedgingBot,
  ]);

  const previousHedgingModeRef = useRef(hedgingMode);
  useEffect(() => {
    const previousHedgingMode = previousHedgingModeRef.current;
    const shouldInitializeHedgingRangeDefaults =
      fieldsToInitialize?.has("hedgingGridsData");
    if (
      !isHedgingBot ||
      (!shouldInitializeHedgingRangeDefaults &&
        previousHedgingMode !== multiToggleValue) ||
      hedgingMode !== HedgingMode.RANGE
    ) {
      previousHedgingModeRef.current = hedgingMode;
      return;
    }
    if (setDefaultGridsData(false, true)) {
      setLongHedgingRatio(50);
      removeFieldToInitialize?.("hedgingGridsData");
      previousHedgingModeRef.current = hedgingMode;
    }
  }, [
    hedgingMode,
    isHedgingBot,
    botAmountData,
    symbolPriceData,
    defaultRangeCurrentPrice,
    fieldsToInitialize,
    removeFieldToInitialize,
  ]);

  const strategyModified = useMemo(
    () =>
      updateFromStrategy &&
      !isEqual(lastUpdatedStrategyRef.current, entryStrategy),
    [entryStrategy]
  );

  useEffect(() => {
    if (strategyModified && activeGridsData) {
      const updatedGridsData = getGridsDataFromStrategy(
        entryStrategy,
        isReverse,
        getGridsParsedBasePrice(activeGridsData, isReverse),
        pricePrecision
      );
      if (updatedGridsData) {
        if (activeGridsData.gridType === GridType.CUSTOM) {
          updatedGridsData.gridType = GridType.CUSTOM;
        }
        setActiveGridsData(
          (prevGridsData: GridsData | HedgingGridsData | undefined) => ({
            ...prevGridsData,
            ...updatedGridsData,
          })
        );
      }
      lastUpdatedStrategyRef.current = entryStrategy;
    }
  }, [entryStrategy]);

  const setGridsDataValue = (key: keyof HedgingGridsData, value: any) => {
    setActiveGridsData(
      (prevGridsData: GridsData | HedgingGridsData | undefined) => {
        let updatedGridsData: HedgingGridsData = {
          ...prevGridsData,
          [key]: value,
        };
        const { isValid } = validateGridsData(updatedGridsData);
        const shouldUpdateStrategy =
          key !== "lowerPriceLongExposureRatio" &&
          key !== "upperPriceLongExposureRatio" &&
          !rangeStrategySectionIsMultiValue;
        if (isValid && shouldUpdateStrategy) {
          const { updatedStrategy, updatedGridsData: nextGridsData } =
            getUpdatedStrategyAndGridsData(
              updatedGridsData,
              entryStrategy,
              isReverse,
              pricePrecision,
              updatedGridsData.gridType !== activeGridsData?.gridType
            );
          updatedGridsData = {
            ...updatedGridsData,
            ...nextGridsData,
          };
          onStrategyChange(updatedStrategy);
        }
        return updatedGridsData;
      }
    );
  };

  const numGrids = activeGridsData?.numGrids || "";
  const numGridsSelectorData = {
    suffix: "3-50",
    value: isSymbol(numGrids) ? undefined : numGrids,
    placeholder: isSymbol(numGrids) ? t("labels.multi") : undefined,
    onChange: (value: string | undefined) =>
      setGridsDataValue("numGrids", value),
  };

  const gridTypeOptions = [GridType.ARITHMETIC, GridType.GEOMETRIC];
  if (isAdvancedView) gridTypeOptions.push(GridType.CUSTOM);
  const gridType = activeGridsData?.gridType;
  const gridTypeSelectorData = {
    title: t("grids.grid_type"),
    shouldDisplay: isAdvancedView,
    value: isSymbol(gridType) ? undefined : gridType,
    placeholder: isSymbol(gridType) ? t("labels.multi") : undefined,
    onChange: (value: string | undefined) => {
      setGridsDataValue("gridType", value);
      if (value === GridType.CUSTOM && tab !== Steps.ENTRIES) {
        setTab(Steps.ENTRIES);
      }
    },
    options: gridTypeOptions,
  };

  const onPriceRangeChange = (
    value: string | undefined,
    isMaxPrice?: boolean
  ) => {
    const prevValue = activeGridsData?.priceRange || {};
    const updatedValue = {
      ...prevValue,
      [isMaxPrice ? "max" : "min"]: value,
    };
    setGridsDataValue("priceRange", updatedValue);
  };

  const getPriceDisabledMessage = () => {
    openMessage(
      `When using a Custom grid type, you can only change the ${
        isReverse ? "high" : "low"
      } price by changing the price difference or price scale.`,
      "warning"
    );
  };

  const getPriceRangeSelectorData = () => {
    const isCustomGridType = activeGridsData?.gridType === GridType.CUSTOM;
    const minDisabled = isCustomGridType && !isReverse;
    const maxDisabled = isCustomGridType && isReverse;
    const percentChange = getPriceRangeDistancePercent(
      activeGridsData?.priceRange
    );
    const min = activeGridsData?.priceRange?.min ?? "";
    const max = activeGridsData?.priceRange?.max ?? "";
    return {
      min: {
        onChange: (value: string | undefined) =>
          onPriceRangeChange(value, false),
        value: isSymbol(min) ? undefined : min,
        disabled: minDisabled,
        onClick: minDisabled ? getPriceDisabledMessage : undefined,
        color: minDisabled
          ? theme.colors.secondaryGrey
          : theme.colors.primaryGrey,
        placeholder: isSymbol(min) ? t("labels.multi") : undefined,
      },
      max: {
        onChange: (value: string | undefined) =>
          onPriceRangeChange(value, true),
        value: isSymbol(max) ? undefined : max,
        disabled: maxDisabled,
        onClick: maxDisabled ? getPriceDisabledMessage : undefined,
        color: maxDisabled
          ? theme.colors.secondaryGrey
          : theme.colors.primaryGrey,
        placeholder: isSymbol(max) ? t("labels.multi") : undefined,
      },
      precision: pricePrecision,
      percentChange,
      helperData: getHelperData("maxPriceDifference"),
    };
  };

  const hedgingExposureSelectorData = isHedgingBot
    ? {
        lower: {
          value: hedgingGridsData?.lowerPriceLongExposureRatio || "",
          onChange: (value: string | undefined) =>
            setGridsDataValue("lowerPriceLongExposureRatio", value),
        },
        upper: {
          value: hedgingGridsData?.upperPriceLongExposureRatio || "",
          onChange: (value: string | undefined) =>
            setGridsDataValue("upperPriceLongExposureRatio", value),
        },
      }
    : undefined;
  return {
    numGridsSelectorData,
    gridTypeSelectorData,
    priceRangeSelectorData: getPriceRangeSelectorData(),
    hedgingExposureSelectorData,
    rangeStrategySectionOverrideData: isHedgingBot
      ? {
          isMultiValue: rangeStrategySectionIsMultiValue,
          disabled: isSymbol(symbols),
          onOverride: () => setDefaultGridsData(false, false),
        }
      : undefined,
    numGridsError,
    priceErrorMessage,
    exposureErrorMessage,
    generalGridsError,
  };
};

export default useGridsSelector;
